<script setup lang="ts">
const {
  modelValue = "",
  invalid = false,
  disabled = false,
  required = false,
  autofocus = false,
  type = "text",
  autocomplete = undefined,
  theme = "main",
} = defineProps<{
  modelValue?: string | number;
  invalid?: boolean;
  disabled?: boolean;
  required?: boolean;
  autofocus?: boolean;
  type?: string;
  autocomplete?: string | undefined;
  theme?: "main" | "auth" | "overlay" | "chat";
}>();

const input = useTemplateRef("input");

const classObject = computed(() => ({
  [`is-theme-${theme}`]: theme,
  [`is-invalid`]: invalid,
  [`is-disabled`]: disabled,
}));

const emit = defineEmits(["update:modelValue", "focus", "blur"]);

const updateValue = (event: Event) => {
  emit("update:modelValue", (event.target as HTMLInputElement).value);
};

const focusInput = async () => {
  if (input.value) {
    await nextTick();
    input.value.focus();
  }
};

defineExpose({
  focus: () => {
    focusInput();
  },
});

watch(
  () => disabled,
  (disabled) => {
    // Autofocus input when it is enabled and autofocus is set
    if (!disabled && autofocus) {
      focusInput();
    }
  },
);
</script>

<template>
  <div class="form-input" :class="classObject">
    <input
      v-bind="$attrs"
      ref="input"
      class="element"
      :value="modelValue"
      :disabled="disabled"
      :required="required"
      :type="type"
      :autocomplete="autocomplete"
      :autofocus="autofocus"
      @input="updateValue"
      @focus="emit('focus')"
      @blur="emit('blur')"
    />
  </div>
</template>

<style lang="scss" scoped>
.form-input {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  height: 3rem;
  box-sizing: border-box;
  border: solid 2px var(--main-input-border-color);
  border-radius: var(--main-border-radius);
  outline: none;
  background: var(--main-input-background-color);
  transition: border-color 100ms ease;

  .element {
    width: 100%;
    height: 100%;
    border: none;
    background: none;
    color: var(--main-input-text-color);
    font-size: 16px;
    font-family: "Manrope", sans-serif;
    appearance: none;
    padding: 0 0.75em;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: rgba(0, 0, 0, 0.3);
    }
  }

  &:focus-within {
    border-color: var(--main-input-focus-color);
  }

  &.is-disabled {
    opacity: 0.5;
  }

  &.is-invalid {
    border-color: var(--main-error-color);

    .element {
      color: var(--main-error-color);
    }
  }

  &.is-theme-auth {
    border-color: var(--auth-input-border-color);
    background-color: var(--auth-input-background-color);

    .element {
      color: var(--auth-input-text-color);
    }

    &:focus-within {
      border-color: var(--auth-input-focus-color);
    }
  }

  &.is-theme-overlay {
    border-color: var(--overlay-input-border-color);
    background-color: var(--overlay-input-background-color);

    .element {
      color: var(--overlay-input-text-color);
    }

    &:focus-within {
      border-color: var(--overlay-input-focus-color);
    }
  }

  &.is-theme-chat {
    border-color: var(--chat-input-border-color);
    border-right: none;
    border-radius: var(--main-border-radius) 0 0 var(--main-border-radius);
    background-color: var(--chat-input-background-color);

    .element {
      color: var(--chat-input-text-color);
    }

    &:focus-within {
      border-color: var(--chat-input-focus-color);
    }
  }

  &.is-theme-auth.is-invalid {
    border-color: var(--auth-error-color);

    .element {
      color: var(--auth-error-color);
    }
  }

  &.is-theme-overlay.is-invalid {
    border-color: var(--overlay-error-color);

    .element {
      color: var(--overlay-error-color);
    }
  }

  &.is-theme-chat.is-invalid {
    border-color: var(--main-error-color);

    .element {
      color: var(--main-error-color);
    }
  }
}
</style>
